import { store } from './store.js';
// ========================================
// PASO 1: COMPRENSIÓN
// ========================================
import { wizardState, getString } from './wizardState.js';
import { safeSetHTML } from './security.js';

export function initStep1() {
    // Mostrar pregunta de comprensión interactiva
    const state = store.state;
    if (!state || !state.problems || state.problems.length === 0) return;
    const problem = state.problems[state.currentProblem];
    if (!problem) return;

    // Extraer datos estructurados del problema (Inicio, Cambio, Pregunta)
    const ayuda = getDecompositionData(problem);

    const quizContainer = document.getElementById('comprehensionQuiz');
    const optionsContainer = document.getElementById('comprehensionOptions');
    const titleEl = document.getElementById('comprehensionQuizTitle');
    const confirmBtn = document.getElementById('comprehendedBtn');
    
    if (quizContainer && optionsContainer && confirmBtn) {
        // Quiz oculto por defecto
        quizContainer.classList.add('hidden');
        confirmBtn.disabled = false; // Habilitado por defecto para avanzar directo
        
        const helpQuestionsBtn = document.getElementById('helpQuestionsBtn');
        if (helpQuestionsBtn) {
            helpQuestionsBtn.onclick = (e) => {
                e.preventDefault();
                quizContainer.classList.remove('hidden');
                confirmBtn.disabled = true; // Requiere responder las preguntas
                if (!store.state.answers) {
                    store.state.answers = {};
                }
                store.state.answers.requestedHelpInStep1 = true;
            };
        }

        const helpBtn = document.getElementById('helpBtn');
        if (helpBtn) {
            helpBtn.onclick = (e) => {
                e.preventDefault();
                if (!store.state.answers) {
                    store.state.answers = {};
                }
                store.state.answers.requestedHelpInStep1 = true;

                if (window.showProblemHelpModal) window.showProblemHelpModal();
            };
        }

        const questions = generateQuizQuestions(problem, ayuda);

        const renderQuestion = (index) => {
            const qData = questions[index];
            if (!qData) return;

            if (titleEl) {
                titleEl.innerHTML = `<span style="background: #0284c7; color: white; border-radius: 20px; padding: 2px 10px; font-size: 0.85rem; font-weight: 800;">${index + 1}/3</span> <span>${qData.title}</span>`;
            }

            safeSetHTML(optionsContainer, '');

            qData.options.forEach(opt => {
                const btn = document.createElement('button');
                btn.className = 'btn btn-secondary no-audio';
                btn.style.textAlign = 'left';
                btn.style.padding = '14px 18px';
                btn.style.fontSize = '1.1rem';
                btn.style.whiteSpace = 'normal';
                btn.style.borderRadius = '12px';
                btn.style.transition = 'all 0.2s ease';
                btn.innerHTML = `<span>${opt.text}</span> <span class="audio-btn-inline" style="float:right; cursor:pointer;" onclick="event.stopPropagation(); window.speak('${opt.text.replace(/'/g, "\\'")}', window.currentLanguage)">🔊</span>`;
                
                btn.onclick = (e) => {
                    if (e.target.closest('.audio-btn-inline')) return;
                    Array.from(optionsContainer.children).forEach(b => b.classList.remove('selected', 'correct', 'error'));
                    btn.classList.add('selected');
                    
                    if (opt.isCorrect) {
                        btn.classList.add('correct');
                        btn.style.background = '#dcfce7';
                        btn.style.borderColor = '#22c55e';
                        btn.style.color = '#15803d';
                        if (window.playSuccess) window.playSuccess();

                        if (index < questions.length - 1) {
                            setTimeout(() => {
                                renderQuestion(index + 1);
                            }, 650);
                        } else {
                            confirmBtn.style.display = 'none';
                            if (window.showWizardFeedback) {
                                window.showWizardFeedback(getString('feedbackPerfect'), true, true);
                            }
                            setTimeout(() => {
                                window.routeNextWizardStep(1);
                            }, 1000);
                        }
                    } else {
                        btn.classList.add('error');
                        btn.style.background = '#fee2e2';
                        btn.style.borderColor = '#ef4444';
                        btn.style.color = '#b91c1c';
                        confirmBtn.disabled = true;
                        if (window.playError) window.playError();
                    }
                };
                optionsContainer.appendChild(btn);
            });
        };

        renderQuestion(0);
    }
}

function generateQuizQuestions(problem, ayuda) {
    const startText = ayuda.inicio.texto || 'Situación inicial del problema';
    const changeText = ayuda.cambio.texto || 'El cambio que ocurrió después';
    const questionText = ayuda.pregunta.texto || '¿Cuántos hay en total?';

    const startDistractors = [
        'Había 10 globos de colores en la fiesta',
        'Estaban jugando juntos en el parque',
        'Había 5 coches aparcados en la calle',
        'Tenían 12 caramelos guardados en la mesa'
    ];

    const changeDistractors = [
        'Compraron 20 refrescos en la tienda',
        'Regalaron 3 juguetes a sus amigos',
        'Se fueron a descansar a casa',
        'Encontraron 4 pegatinas en la bolsa'
    ];

    const questionDistractors = [
        '¿De qué color es el cielo?',
        '¿Qué comieron ayer por la tarde?',
        '¿Dónde viven los personajes?',
        '¿Cuántos años tienen los niños?'
    ];

    const pickDistractor = (list, avoidText) => {
        const filtered = list.filter(item => item.toLowerCase().trim() !== avoidText.toLowerCase().trim());
        return filtered[Math.floor(Math.random() * filtered.length)] || list[0];
    };

    return [
        {
            id: 'start',
            title: '1º ¿Qué había o sucedía al principio?',
            options: [
                { text: startText, isCorrect: true },
                { text: pickDistractor(startDistractors, startText), isCorrect: false }
            ].sort(() => Math.random() - 0.5)
        },
        {
            id: 'change',
            title: '2º ¿Qué ocurrió o cambió después?',
            options: [
                { text: changeText, isCorrect: true },
                { text: pickDistractor(changeDistractors, changeText), isCorrect: false }
            ].sort(() => Math.random() - 0.5)
        },
        {
            id: 'question',
            title: '3º ¿De qué trata la pregunta del problema?',
            options: [
                { text: questionText, isCorrect: true },
                { text: pickDistractor(questionDistractors, questionText), isCorrect: false }
            ].sort(() => Math.random() - 0.5)
        }
    ];
}

function getDecompositionData(problem) {
    let statement = '';
    if (typeof problem.statement === 'string') {
        statement = problem.statement;
    } else if (problem.statement) {
        statement = problem?.statement?.[window.currentLanguage] || problem?.statement?.['es-ES'] || Object.values(problem.statement)[0] || '';
    }

    const questionMatch = statement.match(/[¿]([^?]+)[?]/);
    const question = questionMatch ? questionMatch[0] : '';

    const usefulData = problem.data && problem.data.useful ? problem.data.useful : [];
    const data1 = usefulData[0] !== undefined ? usefulData[0] : 0;
    const data2 = usefulData[1] !== undefined ? usefulData[1] : 0;

    const textWithoutQuestion = statement.replace(/[¿][^?]+[?]/, '').trim();
    // Split simple por conectores comunes para 1º de primaria
    const parts = textWithoutQuestion.split(/\s+(?:y|pero|,\s*pero|entonces|ahora)\s+/);
    const startText = parts[0] || '';
    const changeText = parts[1] || '';

    const selectedEmoji = typeof window.getBestEmoji === 'function' ? window.getBestEmoji(statement) : 'UNIT_SQUARE';

    return {
        inicio: { texto: startText, cantidad: data1, emoji: selectedEmoji },
        cambio: {
            texto: changeText,
            cantidad: data2,
            emoji: selectedEmoji,
            operacion: (problem.operation === 'resta' || problem.operation === '-') ? '-' : '+'
        },
        pregunta: { texto: question }
    };
}

function generateTwemojiHTML(emojiCode, count) {
    if (count <= 0) return '';
    const TWEMOJI_CDN = 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14/assets/svg/';
    if (!emojiCode || emojiCode === 'UNIT_SQUARE' || emojiCode === '2753') {
        let html = '';
        const displayCount = Math.min(count, 10);
        for (let i = 0; i < displayCount; i++) {
            html += `<div class="unit-square" style="display:inline-block; margin:2px;"></div>`;
        }
        if (count > 10) html += `<span style="font-size:0.8rem; font-weight:800; margin-left:4px;">+${count - 10}</span>`;
        return html;
    }

    let html = '';
    const displayCount = Math.min(count, 10); // Limit count for UI clarity

    for (let i = 0; i < displayCount; i++) {
        html += `<img class="twemoji" src="${TWEMOJI_CDN}${emojiCode}.svg" style="width:20px; height:20px; margin:2px;" onerror="this.outerHTML='<div class=\\'unit-square\\' style=\\'display:inline-block; margin:2px;\\'></div>'">`;
    }
    if (count > 10) html += `<span style="font-size:0.8rem; font-weight:800; margin-left:4px;">+${count - 10}</span>`;

    return html;
}

export function confirmComprehension(understood) {
    if (understood) {
        wizardState.data.comprehension = true;

        if (window.showWizardFeedback) {
            window.showWizardFeedback(getString('feedbackPerfect'), true, true);
        }

        // Marcar paso como completado
        if (window.markStepComplete) {
            window.markStepComplete(1);
        }

        // Esperar y avanzar al paso 2
        setTimeout(() => {
            if (window.goToWizardStep) {
                window.routeNextWizardStep(1);
            }
        }, 2000);
    }
}
